/**
 * Select component with search support and accessibility
 */
import React, { useState, useRef, useEffect } from 'react';
import { colors, spacing, typography, borderRadius, transitions, shadows, typographyFlat } from '../../styles/tokens';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  searchable?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  searchable = false,
  disabled = false,
  required = false,
  fullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  
  const selectedOption = options.find((opt) => opt.value === value);
  
  const filteredOptions = searchable
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: fullWidth ? '100%' : `calc(${spacing.lg} * 5)`,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: spacing.xs,
    // font family inherits from global styles
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  };

  const triggerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.background,
    border: `2px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: typography.body.fontSize,
    color: selectedOption ? colors.text : colors.textSecondary,
    transition: `border-color ${transitions.base}`,
    height: `${spacing.xl}`,
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: spacing.xs,
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    boxShadow: shadows.lg,
    maxHeight: `calc(${spacing.lg} * 6)`,
    overflowY: 'auto',
    zIndex: 1000,
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    padding: spacing.sm,
    border: 'none',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typography.small.fontSize,
    outline: 'none',
  };

  const optionStyle = (isSelected: boolean, isDisabled: boolean): React.CSSProperties => ({
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    backgroundColor: isSelected ? colors.primary[50] : 'transparent',
    color: isDisabled ? colors.textSecondary : colors.text,
    fontSize: typography.small.fontSize,
    transition: `background-color ${transitions.fast}`,
  });

  return (
    <div ref={containerRef} style={containerStyle}>
      {label && (
        <label style={labelStyle}>
          {label}
          {required && <span aria-label="required"> *</span>}
        </label>
      )}
      <div
        style={triggerStyle}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled}
      >
        <span>{selectedOption?.label || placeholder}</span>
        <span aria-hidden="true">{isOpen ? '▲' : '▼'}</span>
      </div>
      
      {isOpen && (
        <div style={dropdownStyle} role="listbox">
          {searchable && (
            <input
              type="text"
              style={searchInputStyle}
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              aria-label="Search options"
            />
          )}
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              style={optionStyle(option.value === value, !!option.disabled)}
              onClick={() => {
                if (!option.disabled) {
                  onChange(option.value);
                  setIsOpen(false);
                  setSearchTerm('');
                }
              }}
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled}
            >
              {option.label}
            </div>
          ))}
          {filteredOptions.length === 0 && (
            <div style={{ padding: spacing.md, textAlign: 'center', color: colors.textSecondary }}>
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};
